// ============================================================================
// AI SUGGESTIONS PANEL - Shows AI suggestions for selected entity
// ============================================================================

import { useState } from 'react'
import { X, Sparkles, Plus, Pencil, AlertCircle, Loader2 } from 'lucide-react'
import type { EntityTreeInstance, EntityTreeClassGroup } from '@/lib/api/client'
import type { EntityDomain } from '@ispot/shared'

// ============================================================================
// TYPES
// ============================================================================

export interface AISuggestion {
  id: string
  type: 'related' | 'refinement' | 'gap'
  name: string
  reason: string
  confidence?: number
  entityType?: string
  domain?: EntityDomain
}

/** Selection can be either a class (entity type) or an instance */
export interface SelectedItem {
  type: 'class' | 'instance'
  label: string
  domain: EntityDomain
  classUri?: string
  className?: string
  uri?: string
}

export interface AISuggestionsPanelProps {
  /** Selected item (class or instance) to show suggestions for */
  selectedItem: SelectedItem | null
  /** AI suggestions for the selected item */
  suggestions: AISuggestion[]
  /** Loading state */
  isLoading?: boolean
  /** Callback when panel is closed */
  onClose: () => void
  /** Callback when a suggestion is accepted (add to tree) */
  onAcceptSuggestion?: (suggestion: AISuggestion) => void
  /** Callback when a suggestion is dismissed */
  onDismissSuggestion?: (suggestionId: string) => void
  /** Custom class name */
  className?: string
}

// ============================================================================
// DOMAIN COLORS
// ============================================================================

const DOMAIN_DOT_CLASSES: Record<EntityDomain, string> = {
  market: 'bg-market',
  business: 'bg-business',
  solution: 'bg-solution',
}

const SUGGESTION_TYPE_STYLES: Record<AISuggestion['type'], { bg: string; text: string; label: string }> = {
  related: { bg: 'bg-market/10', text: 'text-market', label: 'Related' },
  refinement: { bg: 'bg-warm-100', text: 'text-warm-700', label: 'Refinement' },
  gap: { bg: 'bg-solution/10', text: 'text-solution', label: 'Gap' },
}

// ============================================================================
// COMPONENT
// ============================================================================

export function AISuggestionsPanel({
  selectedItem,
  suggestions,
  isLoading = false,
  onClose,
  onAcceptSuggestion,
  onDismissSuggestion,
  className = '',
}: AISuggestionsPanelProps) {
  const [acceptingId, setAcceptingId] = useState<string | null>(null)

  if (!selectedItem) return null

  const relatedSuggestions = suggestions.filter((s) => s.type === 'related')
  const refinementSuggestions = suggestions.filter((s) => s.type === 'refinement')
  const gapSuggestions = suggestions.filter((s) => s.type === 'gap')

  const handleAccept = async (suggestion: AISuggestion) => {
    if (!onAcceptSuggestion) return
    setAcceptingId(suggestion.id)
    try {
      await onAcceptSuggestion(suggestion)
    } finally {
      setAcceptingId(null)
    }
  }

  return (
    <div className={`w-80 bg-white rounded-lg border border-warm-200 shadow-sm overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-warm-100 to-warm-50 border-b border-warm-200">
        <div className="w-7 h-7 rounded-lg bg-warm-600 flex items-center justify-center text-white">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-semibold text-sm text-warm-800">AI Suggestions</h3>
          <p className="text-[10px] text-warm-500">Based on your selection</p>
        </div>
        <button
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded text-warm-400 hover:bg-warm-100 hover:text-warm-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Selected item */}
      <div className="px-4 py-3 bg-warm-50 border-b border-warm-200">
        <div className="text-[10px] uppercase tracking-wide text-warm-500 mb-1">
          {selectedItem.type === 'class' ? 'Selected Entity Type' : 'Selected Entity'}
        </div>
        <div className="flex items-center gap-2 font-medium text-sm text-warm-800">
          <span className={`w-2 h-2 rounded-full ${DOMAIN_DOT_CLASSES[selectedItem.domain]}`} />
          <span className="truncate">{selectedItem.label}</span>
        </div>
      </div>

      {/* Suggestions content */}
      <div className="max-h-96 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8 text-warm-400">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            <span className="text-xs">
              {selectedItem.type === 'class' ? 'Analyzing entity type...' : 'Analyzing entity...'}
            </span>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="py-8 text-center text-sm text-warm-400">
            No suggestions available
          </div>
        ) : (
          <>
            {/* Related entities */}
            {relatedSuggestions.length > 0 && (
              <SuggestionSection
                title="Related Entities"
                icon={<Plus className="w-3 h-3" />}
                suggestions={relatedSuggestions}
                onAccept={handleAccept}
                onDismiss={onDismissSuggestion}
                acceptingId={acceptingId}
              />
            )}

            {/* Refinement suggestions */}
            {refinementSuggestions.length > 0 && (
              <SuggestionSection
                title="Refinement Suggestions"
                icon={<Pencil className="w-3 h-3" />}
                suggestions={refinementSuggestions}
                onAccept={handleAccept}
                onDismiss={onDismissSuggestion}
                acceptingId={acceptingId}
              />
            )}

            {/* Coverage gaps */}
            {gapSuggestions.length > 0 && (
              <SuggestionSection
                title="Coverage Gaps"
                icon={<AlertCircle className="w-3 h-3" />}
                suggestions={gapSuggestions}
                onAccept={handleAccept}
                onDismiss={onDismissSuggestion}
                acceptingId={acceptingId}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// SUGGESTION SECTION
// ============================================================================

interface SuggestionSectionProps {
  title: string
  icon: React.ReactNode
  suggestions: AISuggestion[]
  onAccept: (suggestion: AISuggestion) => void
  onDismiss?: (id: string) => void
  acceptingId: string | null
}

function SuggestionSection({
  title,
  icon,
  suggestions,
  onAccept,
  onDismiss,
  acceptingId,
}: SuggestionSectionProps) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center gap-1.5 text-xs font-medium text-warm-600 mb-2">
        <span className="text-warm-600">{icon}</span>
        {title}
      </div>
      <div className="space-y-2">
        {suggestions.map((suggestion) => (
          <SuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            onAccept={() => onAccept(suggestion)}
            onDismiss={onDismiss ? () => onDismiss(suggestion.id) : undefined}
            isAccepting={acceptingId === suggestion.id}
          />
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// SUGGESTION CARD
// ============================================================================

interface SuggestionCardProps {
  suggestion: AISuggestion
  onAccept: () => void
  onDismiss?: () => void
  isAccepting: boolean
}

function SuggestionCard({ suggestion, onAccept, onDismiss, isAccepting }: SuggestionCardProps) {
  const typeStyle = SUGGESTION_TYPE_STYLES[suggestion.type]

  return (
    <div className="p-3 bg-warm-50 border border-warm-200 rounded-lg hover:border-warm-400 transition-colors">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${typeStyle.bg} ${typeStyle.text}`}>
          {typeStyle.label}
        </span>
        {suggestion.confidence && (
          <span className="text-[10px] text-warm-400">{Math.round(suggestion.confidence * 100)}% match</span>
        )}
      </div>

      {/* Content */}
      <div className="text-xs font-medium text-warm-800 mb-1">{suggestion.name}</div>
      <div className="text-[11px] text-warm-500 mb-2">{suggestion.reason}</div>

      {/* Actions */}
      {suggestion.type === 'related' && (
        <div className="flex gap-2 pt-2 border-t border-warm-200">
          <button
            onClick={onAccept}
            disabled={isAccepting}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium bg-market text-white rounded hover:bg-market/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAccepting ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <Plus className="w-3 h-3" />
                Add to Tree
              </>
            )}
          </button>
          {onDismiss && (
            <button
              onClick={onDismiss}
              disabled={isAccepting}
              className="px-2 py-1.5 text-[10px] font-medium border border-warm-300 text-warm-600 rounded hover:border-warm-400 disabled:opacity-50 transition-colors"
            >
              Later
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default AISuggestionsPanel
